import React, { useState } from 'react';
import { useCompanyStore } from '../../../stores/companyStore';
import { notify } from '../../../stores/notificationStore';
import { confirm } from '../../../stores/confirmStore';
import {
  isVatExempt,
  isIntraCommunity,
  resolveDocumentVatExemptArticle,
  formatDate,
} from '../../../utils/vatCalculations';
import { InvoiceTemplateProps, InvoiceItem } from '../types';
import { ActionBar } from '../shared/ActionBar';
import { ClientInfoSection } from '../shared/ClientInfoSection';
import { ItemsTable } from '../shared/ItemsTable';
import { TotalsSection } from '../shared/TotalsSection';
import { LegalFooter } from '../shared/LegalFooter';
import { UnifiedEmailModal, EmailData } from '../../modals/UnifiedEmailModal';
import { PaymentSchedulesManagement } from '../../../features/payments';
import { publicLinksAPI, invoicesAPI } from '../../../utils/api';
import { logger } from '../../../utils/logger';

/**
 * Template Classique (FREE)
 * - Logo: top-left
 * - Header: standard
 * - Table: striped
 * - Couleur par défaut: Rose (#0D9488) / Violet (#9333ea)
 */
export const InvoiceTemplateClassic: React.FC<InvoiceTemplateProps> = ({
  invoiceData,
  onClose,
  template,
  company: companyProp,
  color: colorProp
}) => {
  const { company: companyStore, invoiceColor: invoiceColorStore } = useCompanyStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPaymentSchedules, setShowPaymentSchedules] = useState(false);
  const [publicLink, setPublicLink] = useState<string>('');

  // Utiliser les props ou fallback sur le store
  const company = companyProp || companyStore;
  const invoiceColor = colorProp || invoiceColorStore || template?.primaryColor || '#0D9488';

  const clientCountryCode = (invoiceData as any)?.Client?.countryCode;
  const isExempt = isVatExempt(company?.vatRegime) || isIntraCommunity(clientCountryCode, parseFloat(String(invoiceData?.totalTVA)) || undefined);
  const vatExemptArticle = resolveDocumentVatExemptArticle(company?.vatRegime, clientCountryCode, parseFloat(String(invoiceData?.totalTVA)) || undefined);

  // Vérification de sécurité
  if (!invoiceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <p className="text-red-600 font-semibold mb-4">Erreur : Aucune donnée de facture</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gradient-to-r from-teal-600 to-purple-600 hover:from-teal-700 hover:to-purple-700 text-white rounded-lg transition-all"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  // Parser les items et normaliser
  const items: InvoiceItem[] = typeof invoiceData.items === 'string'
    ? JSON.parse(invoiceData.items).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),  // ✅ Fallback vers calcul
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),  // ✅ Prisma utilise taxPercent
          vatAmount: item.vatAmount || 0,
        };
      })
    : (invoiceData.items || []).map((item: any) => {
        const unitPrice = Number(item.unitPrice) || 0;
        const quantity = Number(item.quantity) || 0;
        return {
          ...item,
          unitPrice,
          quantity,
          total: Number(item.totalHT) || Number(item.total) || (unitPrice * quantity),  // ✅ Fallback vers calcul
          description: item.description || '',
          vatRate: item.taxPercent || (isExempt ? 0 : 20),  // ✅ Prisma utilise taxPercent
          vatAmount: item.vatAmount || 0,
        };
      });

  // Récupération des données de remise
  const discountType = invoiceData.discountType || 'none';
  const discountValue = parseFloat(String(invoiceData.discountValue)) || 0;
  const discountAmount = parseFloat(String(invoiceData.discountAmount)) || 0;

  // ✅ Construire la ventilation TVA à partir des items (affichage uniquement)
  const vatBreakdown: Array<{ rate: number; baseHT: number; vatAmount: number }> = [];
  const vatByRate: Record<number, { baseHT: number; vatAmount: number }> = {};

  items.forEach((item: InvoiceItem) => {
    const rate = item.vatRate || 0;
    if (!vatByRate[rate]) {
      vatByRate[rate] = { baseHT: 0, vatAmount: 0 };
    }
    vatByRate[rate].baseHT += item.total || 0;
    vatByRate[rate].vatAmount += item.vatAmount || 0;
  });

  Object.entries(vatByRate).forEach(([rate, data]) => {
    vatBreakdown.push({
      rate: Number(rate),
      baseHT: data.baseHT,
      vatAmount: data.vatAmount
    });
  });

  // ✅ Utiliser les valeurs du backend (source de vérité)
  const subtotal = parseFloat(String(invoiceData.totalHT)) || parseFloat(String(invoiceData.subtotal)) || 0;
  const tax = parseFloat(String(invoiceData.totalTVA)) || parseFloat(String(invoiceData.tax)) || 0;
  const total = parseFloat(String(invoiceData.totalTTC)) || parseFloat(String(invoiceData.total)) || 0;

  // Handlers
  const handleGeneratePDF = async () => {
    if (!invoiceData.id) {
      notify.error('ID de la facture manquant', 'Génération PDF');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF (templates uniformisés)
      const blob = await invoicesAPI.downloadPDF(invoiceData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || 'Client';
      a.download = `Facture_${invoiceData.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF généré avec succès', 'Génération PDF');
    } catch (error) {
      logger.error('Erreur génération PDF:', error);
      notify.error("Erreur lors de la génération du PDF : " + (error as Error).message, 'Génération PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateFacturX = async () => {
    if (!invoiceData.id) {
      notify.error('ID de la facture manquant', 'Factur-X');
      return;
    }
    setIsLoading(true);
    try {
      // ✅ Utilisation du backend pour générer le PDF Factur-X (templates uniformisés)
      const blob = await invoicesAPI.downloadFacturXPDF(invoiceData.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientName = (invoiceData as any).Client?.fullName || (invoiceData as any).Client?.companyName || 'Client';
      a.download = `Facture_FacturX_${invoiceData.invoiceNumber}_${clientName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('PDF Factur-X généré avec succès', 'Factur-X');
    } catch (error) {
      logger.error('Erreur Factur-X:', error);
      notify.error("Erreur lors de la génération du PDF Factur-X : " + (error as Error).message, 'Erreur Factur-X');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenEmailModal = async () => {
    try {
      setIsLoading(true);
      // ✅ Utilisation de l'API Layer
      if (!invoiceData.id) {
        throw new Error('ID de la facture manquant');
      }
      const result = await publicLinksAPI.getInvoiceLink(invoiceData.id);
      if (result.success && result.data && result.data.length > 0) {
        const activeLink = result.data.find((link: any) => link.isActive);
        if (activeLink) {
          setPublicLink(activeLink.url);
        }
      }
    } catch (error) {
      logger.error('Erreur lors de la récupération du lien public:', error);
    } finally {
      setIsLoading(false);
      setShowEmailModal(true);
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!invoiceData?.id) {
        throw new Error('ID de la facture manquant');
      }

      // Générer le PDF Factur-X de la facture
      setIsLoading(true);
      const { generateFacturXPDF, convertInvoiceToFacturX } = await import('../../../utils/facturx');
      const facturxData = convertInvoiceToFacturX({ ...invoiceData, items: items }, company);
      const pdfBlob = await generateFacturXPDF(facturxData);

      // Préparer FormData pour l'envoi
      const formData = new FormData();
      formData.append('to', emailData.to);
      formData.append('subject', emailData.subject);
      formData.append('body', emailData.body);
      formData.append('pdf', pdfBlob, `Facture_${invoiceData.invoiceNumber}.pdf`);

      // ✅ Utilisation de l'API Layer
      await publicLinksAPI.sendInvoiceEmail(invoiceData.id, formData);

      notify.success(`Email envoyé à ${emailData.to}`, 'Envoi réussi');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur inconnue';
      notify.error(message, 'Erreur d\'envoi');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen print:min-h-0 print:bg-white">
      {/* Action Bar */}
      <ActionBar
        onGeneratePDF={handleGeneratePDF}
        onGenerateFacturX={handleGenerateFacturX}
        onSendEmail={handleOpenEmailModal}
        onPrint={handlePrint}
        onClose={onClose}
        onPaymentSchedules={() => setShowPaymentSchedules(true)}
        isLoading={isLoading}
        documentType="invoice"
        invoiceStatus={invoiceData.status}
      />

      {/* Email Modal */}
      <UnifiedEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="invoice"
        documentNumber={invoiceData.invoiceNumber}
        clientEmail={invoiceData.clientEmail || ''}
        clientName={invoiceData.clientName || ''}
        showPdfPreview={false}
        templateVariables={{
          invoice_number: invoiceData.invoiceNumber || '',
          client_name: invoiceData.clientName || '',
          total: total.toFixed(2),
          public_link: publicLink,
          due_date: formatDate(invoiceData.dueDate || invoiceData.issueDate),
          payment_conditions: company?.defaultPaymentTermsDays ? `Paiement à ${company.defaultPaymentTermsDays} jours` : 'Paiement à 30 jours',
          iban: company?.iban || '',
          bic: company?.bic || '',
          companyName: company?.legalName || company?.tradeName || '',
          companyEmail: company?.email || '',
          companyPhone: company?.phone || '',
        }}
      />

      {/* Payment Schedules Modal */}
      {showPaymentSchedules && invoiceData.id && (
        <PaymentSchedulesManagement
          invoiceId={invoiceData.id}
          invoiceNumber={invoiceData.invoiceNumber}
          invoiceTotal={total}
          onClose={() => setShowPaymentSchedules(false)}
        />
      )}

      {/* Document Content */}
      <div className="py-8 print:py-0 print:mt-0">
        <div className="max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none print:max-w-full" id="invoice-template">
          <div className="p-4 md:p-8">
            {/* Header avec ou sans logo */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-8 md:mb-12 gap-6">
              {/* Company Info */}
              <div>
                {/* Logo si disponible */}
                {company?.logoUrl && (
                  <div className="mb-1 md:mb-2">
                    <img
                      src={company.logoUrl}
                      alt={company.legalName || company.tradeName || 'Logo'}
                      className="h-16 md:h-20 max-w-[200px] object-contain"
                    />
                  </div>
                )}

                {/* Nom de l'entreprise - grand si pas de logo, normal si logo */}
                {!company?.logoUrl && (
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    {company?.legalName || company?.tradeName}
                  </h2>
                )}

                <div className="text-gray-600 mt-1 md:mt-2 space-y-0.5 md:space-y-1 text-xs md:text-sm">
                  {/* Nom en taille normale si logo présent */}
                  {company?.logoUrl && (
                    <p className="font-bold text-gray-800">
                      {company?.legalName || company?.tradeName}
                    </p>
                  )}

                  {company?.addressLine1 && (
                    <p>{company.addressLine1}</p>
                  )}
                  {((company?.zipCode) || (company?.city)) && (
                    <p>
                      {(company?.zipCode) && `${company.zipCode} - `}
                      {company?.city}
                    </p>
                  )}
                  {company?.phone && (
                    <p>{company.phone}</p>
                  )}
                  {company?.email && (
                    <p style={{ color: invoiceColor }}>{company.email}</p>
                  )}
                </div>
              </div>

              {/* Invoice Number & Dates & Client */}
              <div className="md:text-right">
                {/* FACTURE Badge - Standard style */}
                <div
                  className="text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg mb-3 md:mb-4 flex items-center gap-2 justify-end"
                  style={{ backgroundColor: invoiceColor }}
                >
                  <h3 className="text-xs md:text-base font-bold">FACTURE</h3>
                  <span className="text-xs md:text-sm font-semibold">
                    {invoiceData.invoiceNumber}
                  </span>
                </div>

                {/* Dates */}
                <div className="text-gray-600 space-y-1 md:space-y-2 text-xs md:text-sm">
                  <div>
                    <span className="font-medium">Date : </span>
                    <span>{formatDate(invoiceData.issueDate)}</span>
                  </div>
                  <div>
                    <span className="font-medium">Échéance : </span>
                    <span>{formatDate(invoiceData.dueDate)}</span>
                  </div>

                  {/* Client Info */}
                  <ClientInfoSection data={invoiceData} accentColor={invoiceColor} />
                </div>
              </div>
            </div>

            {/* Reference Information */}
            {(invoiceData.purchaseOrderNumber || invoiceData.reference || invoiceData.projectName) && (
              <div className="mb-6 bg-blue-50 border-l-4 border-blue-600 p-3 md:p-4 rounded">
                <h3 className="text-xs md:text-sm font-semibold text-blue-900 mb-2">📋 Références</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 text-xs md:text-sm text-gray-700">
                  {invoiceData.purchaseOrderNumber && (
                    <div>
                      <span className="font-medium text-blue-800">Bon de Commande :</span>
                      <p className="mt-1 font-semibold">{invoiceData.purchaseOrderNumber}</p>
                    </div>
                  )}
                  {invoiceData.reference && (
                    <div>
                      <span className="font-medium text-blue-800">Référence :</span>
                      <p className="mt-1 font-semibold">{invoiceData.reference}</p>
                    </div>
                  )}
                  {invoiceData.projectName && (
                    <div>
                      <span className="font-medium text-blue-800">Projet :</span>
                      <p className="mt-1 font-semibold">{invoiceData.projectName}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Titre du document */}
            {invoiceData.title && (
              <div
                className="mb-6 text-2xl font-semibold border-l-4 pl-4 py-2 text-gray-800"
                style={{ borderColor: invoiceColor }}
              >
                {invoiceData.title}
              </div>
            )}

            {/* Items Table - Style STRIPED */}
            <ItemsTable
              items={items}
              tableStyle="striped"
              accentColor={invoiceColor}
              showVat={!isExempt}
            />

            {/* Totals Section */}
            <TotalsSection
              subtotal={subtotal}
              tax={tax}
              total={total}
              discount={discountAmount}
              discountType={discountType}
              discountValue={discountValue}
              vatBreakdown={vatBreakdown}
              accentColor={invoiceColor}
              showVatBreakdown={!isExempt}
              isVatExempt={isExempt}
              vatExemptionMention={vatExemptArticle}
            />

            {/* Notes Internes (affichées sur le document) */}
            {invoiceData.internalNotes && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2" style={{ borderColor: invoiceColor }}>
                  Notes
                </h3>
                <p className="text-gray-700 p-4 rounded-lg text-sm whitespace-pre-wrap" style={{ backgroundColor: invoiceColor + '15' }}>
                  {invoiceData.internalNotes}
                </p>
              </div>
            )}

            {/* Mentions particulières */}
            {invoiceData.termsConditions && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2" style={{ borderColor: invoiceColor }}>
                  Mentions particulières
                </h3>
                <p className="text-gray-700 p-4 rounded-lg text-sm whitespace-pre-wrap" style={{ backgroundColor: invoiceColor + '15' }}>
                  {invoiceData.termsConditions}
                </p>
              </div>
            )}

            {/* Legal Footer */}
            <LegalFooter company={company} notes={invoiceData.notes} vatExemptArticle={vatExemptArticle} />
          </div>
        </div>
      </div>
    </div>
  );
};
